/** Compacte hero-kaart voor de actieve focustaak met timer en voortgangsbalk. */
import {
  Checkbox,
  GlobalStyles,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import useCurrentTime from '@/features/tasks/hooks/status/use-task-current-time.js';
import { actionIconButtonSx } from '@/components/ui/actionIconButtonSx';
import { FocusTaskProgress } from '@/features/today/components/FocusTaskProgress';
import {
  FOCUS_TICK_MS,
  buildFocusCaption,
  formatElapsedTime,
  getElapsedSeconds,
  getFocusHeroPaperSx,
} from '@/features/today/components/focusHeroUtils';
import { useT } from '@/i18n/useT';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';

const FocusHero = ({
  task,
  isFocused,
  focusStartTime,
  onToggleComplete,
  onStartFocus,
  onStopFocus,
}) => {
  const theme = useTheme();
  const t = useT();
  const { projectDisplayMap } = useTasksContext();
  const currentTime = useCurrentTime(FOCUS_TICK_MS);
  const { timerRunning, elapsedSeconds } = getElapsedSeconds(
    isFocused,
    focusStartTime,
    currentTime,
  );
  const caption = buildFocusCaption(task, projectDisplayMap);
  const paperSx = getFocusHeroPaperSx({
    theme,
    isFocused,
    focusStartTime,
    task,
  });

  return (
    <>
      <GlobalStyles styles="@property --border-angle { syntax: '<angle>'; inherits: false; initial-value: 0deg; }" />
      <Box
        data-testid="focus-hero"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          mb: 1.5,
          pt: 0.5,
          pb: 0.5,
          bgcolor: 'background.default',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'secondary.main',
            mb: 0.75,
            px: 0.25,
          }}
        >
          {t('focus.inProgress', 'Nu bezig')}
        </Typography>
        <Paper variant="outlined" sx={paperSx}>
          <Checkbox
            size="small"
            checked={!!task.completed}
            onChange={(e) => onToggleComplete(e.target.checked)}
            inputProps={{
              'aria-label': t('focus.complete', 'Taak afronden'),
            }}
          />
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconButton
              size="small"
              onClick={() => (isFocused ? onStopFocus() : onStartFocus())}
              aria-label={
                isFocused
                  ? t('focus.pause', 'Pauzeer timer')
                  : t('focus.start', 'Start timer')
              }
              title={
                isFocused
                  ? t('focus.pause', 'Pauzeer timer')
                  : t('focus.start', 'Start timer')
              }
              sx={{
                borderRadius: '50%',
                p: 0.5,
                ...actionIconButtonSx,
              }}
            >
              {isFocused ? (
                <PauseIcon sx={{ fontSize: 15 }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 15 }} />
              )}
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.95em' }} noWrap>
              {task.title}
            </Typography>
            {caption ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {caption}
              </Typography>
            ) : null}
          </Box>
          <Typography
            sx={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: '0.95em',
              color: timerRunning ? 'text.primary' : 'text.subtle',
              fontVariantNumeric: 'tabular-nums',
              flexShrink: 0,
              fontWeight: 600,
              letterSpacing: '0.02em',
            }}
          >
            {formatElapsedTime(elapsedSeconds)}
          </Typography>
          <IconButton
            size="small"
            onClick={onStopFocus}
            aria-label={t('focus.stop', 'Stop focus (zonder afronden)')}
            title={t('focus.stop', 'Stop focus (zonder afronden)')}
            sx={{ p: 0.5 }}
          >
            <StopIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Paper>
        {timerRunning && task.duration ? (
          <FocusTaskProgress
            focusStartTime={focusStartTime}
            duration={task.duration}
          />
        ) : null}
      </Box>
    </>
  );
};

export default FocusHero;
